import {
  Badge,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuItem,
  SidebarProvider,
  buttonVariants,
  useSidebar,
} from '@folio/components';
import { cn } from '@folio/lib';
import { AlertTriangle } from 'lucide-react';
import { NavLink, Outlet, ScrollRestoration } from 'react-router-dom';
import { LanguageToggle } from '../../features/LanguageToggle';
import { EnvironmentBanner } from '../EnvironmentBanner';
import { ImpersonationBanner } from '../ImpersonationBanner';
import { MobileHeader } from './MobileHeader';
import { SuspenseLoader } from './SuspenseLoader';
import { UserSidebarFooter } from './UserSidebarFooter';
import { Stack } from '@folio/components/ui/flex';

type SidebarItem = {
  label: string;
  icon: React.ReactNode;
  link: string;
  /** Trailing count pill. Omit rather than passing 0. */
  badgeCount?: number;
  /** Tooltip of the count pill, saying what it counts. */
  badgeLabel?: string;
  /** When set, a warning icon is shown on the item; the string is its accessible label. */
  warningLabel?: string;
};

interface ApplicationLayoutProps {
  /**
   * The items to display in the sidebar
   */
  items: Array<SidebarItem>;
  logo: React.ReactNode;
}

const linkClassName = ({ isActive }: { isActive: boolean }) =>
  cn(buttonVariants({ variant: isActive ? 'secondary' : 'ghost' }), 'justify-start w-full');

function SidebarNavigation({ items }: { items: ApplicationLayoutProps['items'] }) {
  const { setOpenMobile, isMobile } = useSidebar();

  const handleLinkClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <SidebarContent>
      <SidebarGroup>
        <Stack asChild>
          <SidebarGroupContent className="p-2">
            <SidebarMenu className="gap-2">
              {items.map(item => (
                <SidebarMenuItem key={item.label}>
                  <NavLink end to={item.link} onClick={handleLinkClick} className={linkClassName}>
                    {({ isActive }) => (
                      <>
                        <span className="mr-3">{item.icon}</span>
                        {item.label}
                        {item.warningLabel ? (
                          <AlertTriangle
                            role="img"
                            aria-label={item.warningLabel}
                            className="ml-auto size-4 text-destructive"
                          />
                        ) : null}
                        {item.badgeCount !== undefined && item.badgeLabel && (
                          <span className="sr-only">{item.badgeLabel}</span>
                        )}
                        {item.badgeCount !== undefined && (
                          <Badge
                            variant="secondary"
                            title={item.badgeLabel}
                            aria-hidden={item.badgeLabel ? true : undefined}
                            className={cn(
                              item.warningLabel ? 'ml-2' : 'ml-auto',
                              // Active row is bg-secondary; swap ground and ink so the pill punches out.
                              isActive &&
                                'bg-secondary-foreground text-secondary hover:bg-secondary-foreground',
                            )}
                          >
                            {item.badgeCount}
                          </Badge>
                        )}
                      </>
                    )}
                  </NavLink>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </Stack>
      </SidebarGroup>
    </SidebarContent>
  );
}

export function ApplicationLayout({ items, logo }: ApplicationLayoutProps) {
  return (
    <SidebarProvider>
      <Sidebar variant="sidebar">
        <SidebarHeader className="border-b p-4">{logo}</SidebarHeader>
        <SidebarNavigation items={items} />
        <SidebarFooter className="border-t p-4 gap-2">
          <LanguageToggle />
          <UserSidebarFooter />
        </SidebarFooter>
      </Sidebar>
      <SidebarInset className="relative bg-gray-50">
        <ImpersonationBanner />
        <EnvironmentBanner />
        <MobileHeader logo={logo} />
        <div className="relative md:container p-1 md:px-4 md:pt-4 pb-20">
          <SuspenseLoader className="absolute">
            <ScrollRestoration />
            <Outlet />
          </SuspenseLoader>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
